import { TARGET } from '@decodo/utils';
import * as React from 'react';

import { useForm, UseFormReturn } from '@/hooks/useForm';

import { LinkButtonStrapiProps } from '@/components/atoms/LinkButton/LinkStrapi';

import { useTranslation } from '@/lib/i18n/i18n.client';
import { Target, targets } from '../constants/targets';
import {
  initFormValidatorForTarget,
  initFormValuesForTarget,
} from '../helpers';

const ScraperContext = React.createContext<
  | {
      target: Target;
      activeTab: 'request' | 'response';
      setActiveTab: React.Dispatch<
        React.SetStateAction<'request' | 'response'>
      >;
      dashboardAction?: LinkButtonStrapiProps;
      setTarget: React.Dispatch<React.SetStateAction<Target>>;
      form: UseFormReturn<Record<string, string | boolean>>;
    }
  | undefined
>(undefined);

function matchScraperToValue(value: TARGET) {
  const match = targets.find((target) => target.name === value);
  if (match) return match;
  return targets[0];
}

export function ScraperProvider({
  children,
  dashboardAction,
  initialTarget,
}: {
  initialTarget: TARGET;
  children: React.ReactNode;
  dashboardAction?: LinkButtonStrapiProps;
}) {
  const { texts } = useTranslation();
  const [activeTab, setActiveTab] = React.useState<'request' | 'response'>(
    'request'
  );
  const [target, setTarget] = React.useState<Target>(
    matchScraperToValue(initialTarget)
  );

  const initialValues = React.useMemo(
    () => initFormValuesForTarget(target) as Record<string, string | boolean>,
    [target]
  );

  const form = useForm<Record<string, string | boolean>>({
    initialValues: initialValues,
    validator: initFormValidatorForTarget(target, texts),
    validateOnChange: true,
  });

  return (
    <ScraperContext
      value={{
        target,
        setTarget,
        activeTab,
        setActiveTab,
        dashboardAction,
        form,
      }}
    >
      {children}
    </ScraperContext>
  );
}

export function useScraper() {
  const context = React.use(ScraperContext);
  if (context === undefined) {
    throw new Error('useScraper must be used within a ScraperProvider');
  }
  return context;
}
